import {
  dsAttendancePathForStatus,
  resolveOpenStationRoute
} from '@/modules/company/PostStation/originRoutes'
import PATHS from '@/router/paths'
import type {
  PriorityAction,
  PriorityPaymentRow
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { priorityStationDateIso } from './rowFormat'

export type PriorityActionKey = PriorityAction | 'COPY_MESSAGE' | 'REGISTER_CONTACT'

export interface PriorityActionDescriptor {
  key: PriorityActionKey
  label: string
  testId: string
}

const testIdFor = (key: PriorityActionKey, doctorStationId: number): string =>
  `priority-payments-action-${key.toLowerCase().replace(/_/g, '-')}-${doctorStationId}`

/** Query que a tela de auditoria lê para oferecer "Voltar para a fila". */
export const PRIORITY_RETURN_TO_PARAM = 'returnTo'

/** Ordem fixa: o olho procura Detalhes no mesmo lugar em toda linha. */
const ACTION_ORDER: PriorityAction[] = ['DETAILS', 'SEND_RECORD', 'AUDIT', 'VIEW_AUDIT']

const labelFor = (action: PriorityAction, row: PriorityPaymentRow): string => {
  switch (action) {
    case 'DETAILS':
      return 'Detalhes'
    case 'SEND_RECORD':
      return row.situation === 'REJECTED' ? 'Reenviar registro' : 'Enviar registro'
    case 'AUDIT':
      return 'Auditar'
    case 'VIEW_AUDIT':
      // Registro ainda na fila: quem não pode decidir só VÊ o que foi enviado.
      return row.situation === 'AWAITING_AUDIT' ? 'Ver registro' : 'Ver auditoria'
  }
}

/**
 * Botões da linha a partir de `allowedActions`. O servidor já resolveu envio
 * (`attendance_emitting`) e auditoria; repetir a conta aqui só criaria um
 * segundo dono da regra — e um botão que aparece e dá 403.
 */
export const priorityRowActions = (row: PriorityPaymentRow): PriorityActionDescriptor[] => {
  const actions: PriorityActionDescriptor[] = ACTION_ORDER.filter((action) =>
    row.allowedActions.includes(action)
  ).map((action) => ({
    key: action,
    label: labelFor(action, row),
    testId: testIdFor(action, row.doctorStationId)
  }))

  /**
   * "Registrar contato" (Q20) não tem entrada em `allowedActions` — o contrato
   * não a define, porque `POST /contacts` é escrita de acompanhamento, não uma
   * decisão sobre o plantão. Ela é DERIVADA de `SEND_RECORD`, que o servidor
   * concedeu: quem pode cobrar o registro é quem registra a cobrança. Isso não
   * é recalcular permissão — nenhuma regra nova é avaliada aqui; se o servidor
   * tirar `SEND_RECORD`, o botão some junto. Copiar a mensagem NÃO registra
   * contato: só abre este formulário.
   */
  if (row.allowedActions.includes('SEND_RECORD')) {
    actions.push({
      key: 'REGISTER_CONTACT',
      label: 'Registrar contato',
      testId: testIdFor('REGISTER_CONTACT', row.doctorStationId)
    })
  }

  return actions
}

/**
 * "Detalhes": reaproveita o deep link "Abrir plantão" do Pós-plantão
 * (`resolveOpenStationRoute`) — um único dono do formato que o Dashboard parseia.
 */
export const priorityDetailsRoute = (
  row: PriorityPaymentRow
): { path: string; query: Record<string, string> } => {
  const { path, query } = resolveOpenStationRoute({
    stationId: row.stationId,
    scheduleId: row.scheduleId,
    dateIso: priorityStationDateIso(row)
  })
  return { path, query: query ?? {} }
}

export const priorityAuditRoute = (
  row: PriorityPaymentRow,
  returnTo?: string | null
): { path: string; query?: Record<string, string> } | null => {
  const record = row.activityRecord
  if (!record) return null

  const path = dsAttendancePathForStatus(record.containerId, record.status)
  return returnTo ? { path, query: { [PRIORITY_RETURN_TO_PARAM]: returnTo } } : { path }
}

/**
 * `returnTo` vem da URL, então é entrada não confiável: só caminho relativo
 * desta fila volta (a própria rota, com query ou hash). URL absoluta e `//host`
 * ficariam fora do router e virariam redirect aberto; `-evil` colado ao prefixo
 * também não passa.
 */
export const isPriorityReturnTo = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const base: string = PATHS.APPROVALS_PRIORITY_PAYMENTS
  if (!value.startsWith(base)) return false
  const next = value.charAt(base.length)
  return next === '' || next === '?' || next === '#'
}

/**
 * `returnTo` da fila a partir da URL atual. Com o hub DESLIGADO a fila vive no
 * shell de abas (`/aprovacoes?tab=priorityPayments&...`), que `isPriorityReturnTo`
 * não aceita: devolve a rota própria da fila com os mesmos filtros — o guard da
 * rota leva de volta ao shell de abas, preservando a query. Mexe só nos
 * segmentos da query, sem decodificar, para não mudar a codificação da URL.
 */
export const priorityQueueReturnTo = (fullPath: string): string => {
  const [beforeHash, hash] = fullPath.split('#', 2)
  const [path, search = ''] = beforeHash.split('?', 2)
  const segments = search ? search.split('&') : []
  const tabSegment = 'tab=priorityPayments'
  if (path !== PATHS.APPROVALS || !segments.includes(tabSegment)) return fullPath
  const rest = segments.filter((segment) => segment !== tabSegment)
  const query = rest.length ? `?${rest.join('&')}` : ''
  return `${PATHS.APPROVALS_PRIORITY_PAYMENTS}${query}${hash !== undefined ? `#${hash}` : ''}`
}
